/**
 * Let a guest buyer into the account their purchase just created.
 *
 * The Stripe webhook creates a USERS row from the guest's email, so by the time
 * the receipt is on screen the buyer HAS an account — they simply have no way in.
 * Today "View Order" pushes them at /account/purchases/<id>, the auth guard sees
 * `unauthenticated`, and they land on a bare login screen with no hint that the
 * address they just typed is the way in.
 *
 * `/api/auth/guest-checkout-complete` exists to answer "has the webhook caught
 * up yet" and returns where to send them. It has been in the tree, schema'd and
 * unit-tested, with nothing calling it.
 *
 * Everything here runs AFTER the card is charged, so every step fails soft: a
 * refusal, a timeout or a webhook that never lands leaves the buyer exactly
 * where the old flow left them, with their receipt and a working View Order
 * button. Nothing in this file may throw.
 */
import { trackError, ErrorSeverity } from './error-tracking';
import { errorText } from './error-message';

/** Where the buyer's new order lives, once the webhook has written it. */
export interface ClaimableOrder {
  email: string;
  redirectUrl: string;
}

export interface WaitForGuestOrderInput {
  paymentIntentId: string;
  email: string;
  /** How many times to ask before giving up. */
  attempts?: number;
  /** Gap between asks, in ms. */
  delayMs?: number;
  /** Injected in tests so they never touch a real clock. */
  wait?: (ms: number) => Promise<void>;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Poll until the webhook has created the account and the transaction, or until
 * the attempts run out.
 *
 * @returns the order to send them to, or null if it never became ready
 */
export async function waitForGuestOrder(
  input: WaitForGuestOrderInput
): Promise<ClaimableOrder | null> {
  const { paymentIntentId, email, attempts = 10, delayMs = 1500, wait = sleep } = input;

  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const response = await fetch('/api/auth/guest-checkout-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentIntentId, email }),
      });
      const data = await response.json();

      if (response.ok && data?.status === 'complete' && data?.redirectUrl) {
        return { email, redirectUrl: data.redirectUrl };
      }

      // 202 means the webhook is still working; anything else is not going to
      // improve by asking again.
      if (response.status !== 202) return null;
    } catch (e) {
      // A dropped request mid-poll is worth one more try, not a thrown error on
      // a page the buyer has already paid on.
      trackError('Failed to ask whether a guest order was ready', ErrorSeverity.WARNING, {
        component: 'claim-guest-order',
        action: 'waitForGuestOrder',
        extra: { error: errorText(e) },
      });
    }

    if (attempt < attempts - 1) await wait(delayMs);
  }

  return null;
}

const PURCHASED_KEY_PREFIX = 'sw:purchased:';

/**
 * Note, for this tab, that the buyer just paid for a listing.
 *
 * The purchase flips the listing to HELD, so the moment they press Back from
 * the login screen the claim flow opened, checkout reloads, sees HELD and tells
 * them the item "has a sale pending and is no longer available" — their own
 * sale. This mark is how checkout tells that buyer apart from everyone else.
 * Session-scoped on purpose: it only has to outlive the next few navigations.
 */
export function rememberPurchase(listingId: string | number | undefined): void {
  if (listingId === undefined || listingId === '') return;
  try {
    window.sessionStorage.setItem(`${PURCHASED_KEY_PREFIX}${listingId}`, '1');
  } catch {
    // Blocked storage costs the buyer the friendlier redirect, nothing more.
  }
}

/** Did this tab just pay for the listing? False whenever storage cannot say. */
export function wasPurchasedHere(listingId: string | number | undefined): boolean {
  if (listingId === undefined || listingId === '') return false;
  try {
    return window.sessionStorage.getItem(`${PURCHASED_KEY_PREFIX}${listingId}`) === '1';
  } catch {
    return false;
  }
}
